import { useCallback, useEffect, useRef, useState } from 'react'
import { dashboardApi } from './api'
import type { Condition } from '@/lib/expr'
import type { DateRangeSpec, Widget, WidgetResult } from './model'

export interface Slot {
    result?: WidgetResult
    /** Transport/validation failure for this tile (distinct from result.error). */
    error?: string
    problems?: string[]
    loading: boolean
    at?: number
    /** Fingerprint of the spec+range this result was computed for. */
    key?: string
}

/** Dashboard-level overrides sent with every query. */
export interface Overrides { dateRange?: DateRangeSpec; filters?: Condition }

const fingerprint = (w: Widget, o: Overrides) => JSON.stringify([w, o])

/** Apply overrides to an unsaved spec the same way the server does for saved ones. */
export function applyOverrides(w: Widget, o: Overrides): Widget {
    let out = w
    if (o.filters) out = { ...out, filters: out.filters ? { all: [out.filters, o.filters] } : o.filters }
    if (o.dateRange) out = { ...out, dateRange: o.dateRange }
    return out
}

/**
 * Loads widget results. View mode runs the SAVED layout through
 * POST /dashboards/{id}/query (server applies the dashboard date override);
 * edit mode runs each (possibly unsaved) spec through preview-widget and only
 * re-runs the ones whose spec changed.
 */
export function useWidgetData(dashboardId: string, widgets: Widget[], editing: boolean, range: Overrides, version: number) {
    const [slots, setSlots] = useState<Record<string, Slot>>({})
    const slotsRef = useRef(slots)
    slotsRef.current = slots
    const tokens = useRef<Record<string, number>>({})

    const patch = useCallback((id: string, s: Partial<Slot>) => {
        setSlots((cur) => ({ ...cur, [id]: { ...cur[id], loading: false, ...s } }))
    }, [])

    const runPreview = useCallback(async (w: Widget) => {
        const tok = (tokens.current[w.id] = (tokens.current[w.id] ?? 0) + 1)
        patch(w.id, { loading: true })
        const spec = applyOverrides(w, range)
        try {
            const result = await dashboardApi.previewWidget(spec)
            if (tokens.current[w.id] === tok) patch(w.id, { result, error: undefined, problems: undefined, loading: false, at: Date.now(), key: fingerprint(w, range) })
        } catch (e) {
            const err = e as { message?: string; problems?: string[] }
            if (tokens.current[w.id] === tok) patch(w.id, { result: undefined, error: err.message ?? 'Failed to load', problems: err.problems, loading: false, at: Date.now(), key: fingerprint(w, range) })
        }
    }, [patch, range])

    const runQuery = useCallback(async (list: Widget[], single?: string) => {
        const toks = new Map(list.map((w) => [w.id, (tokens.current[w.id] = (tokens.current[w.id] ?? 0) + 1)]))
        setSlots((cur) => {
            const next = { ...cur }
            for (const w of list) next[w.id] = { ...next[w.id], loading: true }
            return next
        })
        try {
            const res = await dashboardApi.query(dashboardId, range, single)
            const byId = new Map(res.results.map((r) => [r.widgetId, r]))
            setSlots((cur) => {
                const next = { ...cur }
                for (const w of list) {
                    if (tokens.current[w.id] !== toks.get(w.id)) continue
                    next[w.id] = { result: byId.get(w.id), error: byId.has(w.id) ? undefined : 'No result returned', loading: false, at: Date.now(), key: fingerprint(w, range) }
                }
                return next
            })
        } catch (e) {
            const message = (e as Error).message
            setSlots((cur) => {
                const next = { ...cur }
                for (const w of list) if (tokens.current[w.id] === toks.get(w.id)) next[w.id] = { error: message, loading: false, at: Date.now() }
                return next
            })
        }
    }, [dashboardId, range])

    const widgetsRef = useRef(widgets)
    widgetsRef.current = widgets

    // View mode: reload everything when the range, version or mode changes.
    useEffect(() => {
        if (editing) return
        const list = widgetsRef.current
        if (list.length) void runQuery(list)
    }, [editing, runQuery, version])

    // Edit mode: (re)preview only the specs that changed.
    const specKey = JSON.stringify(widgets)
    useEffect(() => {
        if (!editing) return
        for (const w of widgetsRef.current) {
            if (slotsRef.current[w.id]?.key !== fingerprint(w, range)) void runPreview(w)
        }
    }, [editing, specKey, range, runPreview])

    const refresh = useCallback((w?: Widget) => {
        if (editing) return void (w ? runPreview(w) : widgetsRef.current.forEach((x) => void runPreview(x)))
        if (w) void runQuery([w], w.id)
        else if (widgetsRef.current.length) void runQuery(widgetsRef.current)
    }, [editing, runPreview, runQuery])

    return { slots, refresh }
}
